'use client'

import Link from 'next/link'
import { useEffect } from 'react'
import {
  LuBell,
  LuCalendarCheck,
  LuCamera,
  LuChevronRight,
  LuFileText,
  LuHeadphones,
  LuHighlighter,
  LuLeaf,
  LuLibrary,
  LuLogIn,
  LuLogOut,
  LuMessageCircle,
  LuShieldAlert,
  LuSmartphone,
  /* LuTarget — [2차 개발] 미션, LuTrophy — 랭킹 임시 숨김, */ LuUser,
  LuUserPlus,
} from 'react-icons/lu'

import { Button } from '@/core/components/form/button/button'
import { toast } from '@/core/lib/toast'
import { cn } from '@/core/utils/common'

import SupportSheet from '@/features/veluvit/components/account/support-sheet'
import { TextLink } from '@/features/veluvit/components/overlay/layer-parts'
import PageHeader from '@/features/veluvit/components/shell/page-header'
import { useSafeMode } from '@/features/veluvit/components/shell/safe-toggle'
import { ToggleMark } from '@/features/veluvit/components/ui/atoms'
import GemCount from '@/features/veluvit/components/ui/gem-count'
import { ItemArt } from '@/features/veluvit/components/ui/item-art'
import GemLots from '@/features/veluvit/components/wallet/gem-lots'
import { useAppData } from '@/features/veluvit/hooks/use-app-data'
import {
  logout,
  openAppSheet,
  openAuth,
} from '@/features/veluvit/services/account.actions'
import { openAdultVerify } from '@/features/veluvit/services/preset.actions'
import {
  ensureAccount,
  safe,
} from '@/features/veluvit/services/session.service'
import {
  openAttendance,
  openCharge,
  openHistory /* , openTicketsInfo — [2차 개발] 해금권 */,
} from '@/features/veluvit/services/wallet.actions'
import { closeAll, openSheet } from '@/features/veluvit/stores/overlay.store'

import type { Route } from 'next'
import type { ReactNode } from 'react'

const ROW =
  'group flex w-full min-w-0 cursor-pointer items-center gap-3 border-t border-foreground/[0.06] px-4 py-3.5 text-left text-c15 text-t1 no-underline transition-colors first:border-t-0 hover:bg-foreground/[0.04] focus-visible:bg-foreground/[0.06]'

/**
 * 줄 색 — 기본(흰 글자 · 회색 아이콘) · strong(로그인, 흰 아이콘 + 굵은 흰 글자) · gold(계정 만들기, 금색) · danger(로그아웃, 빨강만).
 * 로그인을 코랄(포인트)로 했더니 빨강 계열이라 로그아웃처럼 보였다(2026-10-07) — 빨강 계열은 로그아웃에만 쓴다
 */
const TONE = {
  base: { icon: 'text-t3', label: '' },
  strong: { icon: 'text-t1', label: 'font-bold text-t1' },
  gold: { icon: 'text-gem', label: 'font-semibold text-gem' },
  danger: { icon: 'text-error', label: 'font-semibold text-error' },
}

type MenuRowProps = {
  icon: ReactNode
  label: string
  sub?: string
  right?: ReactNode
  chevron?: boolean
  href?: Route
  onClick?: () => void
  pressed?: boolean
  tone?: keyof typeof TONE
}

/** 마이 메뉴 한 줄 — 이동형은 Link, 동작형은 button */
function MenuRow({
  icon,
  label,
  sub,
  right,
  chevron,
  href,
  onClick,
  pressed,
  tone = 'base',
}: MenuRowProps) {
  const body = (
    <>
      <span
        aria-hidden="true"
        className={cn(
          'grid w-5 flex-none place-items-center [&>svg]:size-5',
          TONE[tone].icon
        )}
      >
        {icon}
      </span>
      <span className="min-w-0">
        <span className={TONE[tone].label}>{label}</span>
        {sub && (
          <span className="mt-0.5 block text-c12 text-t3 [overflow-wrap:anywhere] over10:text-c13">
            {sub}
          </span>
        )}
      </span>
      {(right !== undefined || chevron) && (
        <span className="ml-auto flex flex-none items-center gap-1 text-c13 leading-none text-t3">
          {right}
          {chevron && (
            <LuChevronRight
              aria-hidden="true"
              className="size-4 text-t4 transition-transform duration-200 ease-smooth group-hover:translate-x-0.5"
            />
          )}
        </span>
      )}
    </>
  )
  if (href)
    return (
      <Link href={href} className={ROW}>
        {body}
      </Link>
    )
  return (
    <Button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={ROW}
      variant="bare"
    >
      {body}
    </Button>
  )
}

/** 메뉴 묶음 — 작은 제목(판 밖) + 옅은 판 한 장. 제목이 없으면 판만(계정 줄) */
function Menu({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section className="min-w-0">
      {title && (
        <h2 className="mb-2 px-1 text-c13 font-semibold text-t3 over10:text-c14">
          {title}
        </h2>
      )}
      <div className="overflow-hidden rounded-panel bg-well">{children}</div>
    </section>
  )
}

/** 바로가기 한 칸 — 아이콘 위 · 이름 아래. 자주 여는 내 보관함 넷을 긴 목록에서 꺼냈다 */
function Shortcut({
  icon,
  label,
  href,
  count,
}: {
  icon: ReactNode
  label: string
  href: Route
  count?: number
}) {
  return (
    <Link
      href={href}
      className="flex min-w-0 flex-col items-center gap-1.5 rounded-panel bg-well px-1 py-3.5 text-center text-t1 no-underline transition-colors hover:bg-foreground/[0.07] focus-visible:bg-foreground/[0.08]"
    >
      <span aria-hidden="true" className="text-t2 [&>svg]:size-5.5">
        {icon}
      </span>
      <span className="w-full truncate text-c12 font-semibold over10:text-c13">
        {label}
        {count !== undefined && (
          <span className="ml-0.5 font-normal text-t3 tabular-nums">
            {count}
          </span>
        )}
      </span>
    </Link>
  )
}

const soon = () => toast.show('이 기능은 준비 중이에요')

const openSupport = safe(async () => {
  await ensureAccount()
  closeAll()
  openSheet(<SupportSheet />, { label: '고객센터' })
})

/**
 * 마이 — 한 줄기로 읽힌다(2026-10-07 "배치가 어지럽다" 다시 잡음):
 *   프로필 → 젬 판 · 해금권 판 → 바로가기 3칸 두 줄(보관함 넷 + 미션 · 유저 랭킹) → 목록(설정 · 고객지원) → 계정 줄.
 * PC 는 두 단 — 왼쪽(360)이 프로필 · 지갑 · 바로가기, 오른쪽(최대 560)은 목록을 한 줄로만 쌓는다.
 * 예전 오른쪽 2×2 격자(길이가 다른 판이 엇갈림)와 '활동' 목록(바로가기 · 젬 내역과 겹침)이 어지러움의 원인이라 둘 다 없앴다.
 * 계정 줄 색 — 로그인 흰색 강조 · 계정 만들기 금색 · 로그아웃만 빨강.
 */
export default function MyScreen() {
  const { data /* , tickets, turnAcc — [2차 개발] 해금권 */ } = useAppData()
  const discord = data?.config.links?.discord
  const user = data?.user || null
  const owned = data?.ownedCards.length || 0
  const initial = user?.name ? Array.from(user.name)[0] : '☾'
  const { showAdult, eligible, toggle: toggleSafe } = useSafeMode()
  const member = !!user && !user.guest

  // /my?login=1&next=/studio-renew… — 앱 밖 화면(스튜디오)이 로그인을 부탁하면 로그인 창을 열고, 로그인 뒤 그 화면으로 돌려보낸다.
  // next 는 우리 경로(/…)만 받는다 — 바깥 주소로 튕기는 오픈 리다이렉트를 막는다
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('login') !== '1') return
    const next = params.get('next') || ''
    const safe = next.startsWith('/') && !next.startsWith('//') ? next : null
    history.replaceState(history.state, '', location.pathname)
    if (user && !user.guest && safe) return void location.assign(safe)
    openAuth('login', safe ? () => location.assign(safe) : undefined)
    // 처음 한 번만 — 계정이 바뀌어 다시 도는 것을 막는다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <PageHeader title="마이" />
      <div className="mx-auto grid max-w-[960px] gap-7 px-4 pt-4 pb-10 over10:grid-cols-[minmax(0,360px)_minmax(0,1fr)] over10:items-start over10:gap-10 over10:px-8 over10:pt-6">
        {/* 왼쪽(모바일은 위) — 나 · 내 지갑 · 바로가기 */}
        {/* sticky 는 뺐다 — 첫 위치가 내려앉아 오른쪽 첫 묶음 제목과 위가 어긋났다(2026-10-07) */}
        <aside className="grid min-w-0 gap-5">
          <div className="flex items-center gap-3.5">
            <span
              aria-hidden="true"
              className="grid size-16 flex-none place-items-center rounded-full bg-foreground/10 text-c26 font-bold text-t1"
            >
              {initial}
            </span>
            <div className="min-w-0">
              <p className="flex items-center gap-1.5">
                <b className="truncate text-c20 leading-[1.25] font-bold over10:text-c22">
                  {user ? user.name : '로그인하고 대화를 이어가세요'}
                </b>
                {user?.guest && (
                  <span className="flex-none rounded-full bg-foreground/10 px-2 py-0.5 text-c11 font-semibold text-t2">
                    게스트
                  </span>
                )}
              </p>
              <p className="mt-0.5 truncate text-c13 text-t3">
                {user
                  ? user.guest
                    ? '게스트로 이용 중이에요'
                    : user.email
                  : '마음에 드는 캐릭터를 만나 보세요'}
              </p>
              <p className="mt-1 text-c12 text-t3 tabular-nums over10:text-c13">
                대화{' '}
                <b className="font-bold text-t1">
                  {data?.wallet.totalTurns || 0}
                </b>
                턴 <span className="mx-1 text-t4">/</span> 모은 장면{' '}
                <b className="font-bold text-t1">{owned}</b>장
              </p>
            </div>
          </div>

          {/* 지갑 — 충전소와 같은 모양(큰 숫자 + 구성 막대) + 버튼 둘. 해금권은 끼워 넣지 않고 아래 따로 */}
          <div className="grid gap-2">
            <section aria-label="내 젬" className="rounded-panel bg-well p-5">
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  <ItemArt name="gem" px={64} className="size-8" />
                  <GemCount className="text-c28 leading-none font-bold tabular-nums" />
                </div>
                <TextLink chevron onClick={openHistory} className="flex-none">
                  내역
                </TextLink>
              </div>
              <GemLots className="mt-4" />
              <div className="mt-5 flex gap-2">
                <Button
                  variant="primary"
                  size="md"
                  shape="pill"
                  bold
                  className="w-full"
                  onClick={openCharge}
                >
                  충전소
                </Button>
                <Button
                  variant="secondary"
                  size="md"
                  shape="pill"
                  bold
                  className="w-full"
                  onClick={openAttendance}
                >
                  <LuCalendarCheck aria-hidden="true" className="size-4" />
                  출석 체크
                </Button>
              </div>
            </section>

            {/* [2차 개발] 해금권 판 — 2026-10-08 2차 범위로 넘김. 되살릴 땐 이 블록 · openTicketsInfo import · tickets · turnAcc 주석을 푼다.
                젬과 다른 재화라 판을 나눈다. 그림 · 장 수 · 다음 1장까지(대화 턴)
            <button
              type="button"
              onClick={openTicketsInfo}
              className="group flex w-full cursor-pointer items-center gap-3 rounded-panel bg-well px-5 py-4 text-left transition-colors hover:bg-foreground/[0.07] focus-visible:bg-foreground/[0.08]"
            >
              <ItemArt name="ticket" px={64} className="size-8 flex-none" />
              <span className="min-w-0 flex-1">
                <span className="block text-c12 text-t3 over10:text-c13">해금권</span>
                <b className="block text-c18 leading-tight font-bold text-t1 tabular-nums">
                  {tickets}
                  <span className="ml-0.5 text-c13 font-semibold text-t2">장</span>
                </b>
              </span>
              <span className="flex-none text-c12 text-t3 tabular-nums over10:text-c13">다음 1장까지 {20 - turnAcc}턴</span>
              <LuChevronRight aria-hidden="true" className="size-4 flex-none text-t4 transition-transform duration-200 ease-smooth group-hover:translate-x-0.5" />
            </button> */}
          </div>

          {/* 바로가기 — 내 보관함. 미션 · 유저 랭킹은 숨김. 예전 '활동' 목록은 여기로 합쳤다(충전 · 사용 내역은 위 젬 판의 '내역'과 같아 뺐다) */}
          <nav aria-label="바로가기" className="grid grid-cols-3 gap-2">
            <Shortcut
              icon={<LuUser />}
              label="페르소나"
              count={data?.personas.length || 0}
              href="/personas"
            />
            <Shortcut icon={<LuCamera />} label="스냅 앨범" href="/snaps" />
            <Shortcut
              icon={<LuHighlighter />}
              label="발췌함"
              href={'/my/excerpts' as Route}
            />
            <Shortcut
              icon={<LuLibrary />}
              label="서재"
              href={'/my/novels' as Route}
            />
            {/* [2차 개발] 미션 — 2026-10-08 2차 범위로 넘김. 되살릴 땐 이 줄 · LuTarget import 주석을 푼다
            <Shortcut icon={<LuTarget />} label="미션" href={'/mission' as Route} /> */}
            {/* 랭킹 임시 숨김
            <Shortcut icon={<LuTrophy />} label="유저 랭킹" href="/ranking/user" /> */}
          </nav>
        </aside>

        {/* 오른쪽(모바일은 아래) — 설정 · 고객지원 · 계정, 한 줄로만 쌓는다 */}
        <div className="grid min-w-0 gap-6 over10:max-w-[560px]">
          <Menu title="설정">
            <MenuRow
              icon={<LuShieldAlert />}
              label="성인 인증"
              right={eligible ? '인증됨' : '인증하기'}
              chevron={!eligible}
              onClick={eligible ? undefined : () => openAdultVerify()}
            />
            <MenuRow
              icon={<LuLeaf />}
              label="세이프 모드"
              sub={
                showAdult
                  ? '언세이프예요 · 19+ 작품도 함께 보는 중'
                  : '세이프예요 · 19+ 작품을 가리고 있어요'
              }
              right={<ToggleMark on={!showAdult} />}
              pressed={!showAdult}
              onClick={toggleSafe}
            />
            <MenuRow
              icon={<LuSmartphone />}
              label="앱 설치"
              chevron
              onClick={() => openAppSheet()}
            />
          </Menu>

          <Menu title="고객지원">
            <MenuRow
              icon={<LuBell />}
              label="공지사항"
              chevron
              onClick={soon}
            />
            <MenuRow
              icon={<LuMessageCircle />}
              label="고객센터"
              chevron
              onClick={openSupport}
            />
            {/* 서버가 주소를 비워 두면(채널 준비 전) 메뉴를 숨긴다 */}
            {discord && (
              <MenuRow
                icon={<LuHeadphones />}
                label="커뮤니티 디스코드"
                chevron
                onClick={() =>
                  window.open(discord, '_blank', 'noopener,noreferrer')
                }
              />
            )}
            <MenuRow
              icon={<LuFileText />}
              label="약관 및 정책"
              chevron
              onClick={soon}
            />
          </Menu>

          {/* 계정 — 다른 줄과 같은 모양, 색만 다르게. 게스트는 로그인(흰색 강조) · 계정 만들기(금색) 두 줄, 회원은 로그아웃(빨강) 한 줄 */}
          <Menu>
            {member ? (
              <MenuRow
                icon={<LuLogOut />}
                label="로그아웃"
                tone="danger"
                onClick={logout}
              />
            ) : (
              <>
                <MenuRow
                  icon={<LuLogIn />}
                  label="로그인"
                  sub="이미 계정이 있어요"
                  tone="strong"
                  chevron
                  onClick={() => openAuth('login')}
                />
                <MenuRow
                  icon={<LuUserPlus />}
                  label="계정 만들기"
                  sub="지금까지의 대화를 그대로 이어가요"
                  tone="gold"
                  chevron
                  onClick={() => openAuth('register')}
                />
              </>
            )}
          </Menu>
        </div>
      </div>
    </>
  )
}
